import { formatMoney } from '@lib/money'
import { isValidCalendarDate } from '@lib/datetime'
import { isPdfMime } from '@lib/mime'
import {
	buildDateRange,
	buildPreviewDays,
	isOnTripDay,
	MAX_PREVIEW_FILES,
	MAX_PREVIEW_IDEAS,
	MAX_PREVIEW_STAYS,
	OverviewSegmentInput,
	stayDateRange,
	sumInCurrency,
	toOverviewFlight,
	toTraveler,
} from './overviewShared'
import {
	AccommodationsSection,
	FilesSection,
	FlightsSection,
	IdeasSection,
	MoneySection,
	OverviewFile,
	OverviewIdea,
	OverviewStay,
	TripOverviewData,
} from './types'

/** Trip fields the overview needs. */
export interface TripOverviewTripInput {
	name: string
	startDate: string | null
	dayCount: number
	defaultCurrencyCode?: string | null
}

/** Member fields the avatar stack needs. */
export interface TripOverviewMemberInput {
	id: number | string
	firstName: string
	lastName: string
	profilePicture?: string | null
}

/** Event fields the itinerary preview needs. */
export interface TripOverviewEventInput {
	id: number | string
	name: string
	tripDayIndex: number
	eventIndex: number
	category?: string | null
	location?: { primaryText?: string | null } | null
	description?: string | null
	tripEventLinks?: readonly unknown[] | null
	fileIds?: readonly unknown[] | null
}

/** Idea fields the ideas grid needs. */
export interface TripOverviewIdeaInput {
	id: number | string
	name: string
	thumbnailUrl?: string | null
}

/** Accommodation fields the stays rows need. */
export interface TripOverviewStayInput {
	id: number | string
	name: string
	city?: string | null
	photoUrl?: string | null
	/** UTC `YYYY-MM-DD HH:MM:SS` instant strings. */
	checkInDate?: string | null
	checkOutDate?: string | null
}

/** Flight fields the flights preview needs (airport codes pre-resolved by the caller). */
export interface TripOverviewFlightInput {
	id: number | string
	airlineIata: string
	flightNumber?: string | null
	originCode?: string | null
	destinationCode?: string | null
	/** Unix seconds (auto flights); `null` → fall back to the wall-clock string. */
	departureTime?: number | null
	/** Wall-clock `YYYY-MM-DD HH:MM:SS` (manual flights). */
	departureTimeStr?: string | null
	durationMinutes?: number | null
}

/** File fields the files preview needs. */
export interface TripOverviewFileInput {
	id: string
	fileName: string
	/** The file's own url. */
	url?: string | null
	/** Preview image url (already gated to image mime types by the caller). */
	thumbnailUrl?: string | null
	/** MIME type; PDFs render the red PDF badge instead of the icon chip. */
	mimeType?: string | null
}

/** Money inputs — the raw expense/pay-up amounts the money tile totals. */
export interface TripOverviewMoneyInput {
	defaultCurrencyCode?: string | null
	expenses?: readonly { amount: number; currencyCode: string }[] | null
	payUps?: readonly { amount: number; currencyCode: string }[] | null
}

/** Everything the normalizer needs, unwrapped from the react-query hooks. */
export interface BuildTripOverviewInput {
	trip: TripOverviewTripInput | null | undefined
	members: readonly TripOverviewMemberInput[] | null | undefined
	events: readonly TripOverviewEventInput[] | null | undefined
	/** Travel segments between events — rendered on the itinerary preview rail. */
	travelSegments?: readonly OverviewSegmentInput[] | null
	ideas?: readonly TripOverviewIdeaInput[] | null
	accommodations?: readonly TripOverviewStayInput[] | null
	flights?: readonly TripOverviewFlightInput[] | null
	files?: readonly TripOverviewFileInput[] | null
	money?: TripOverviewMoneyInput | null
}

const buildIdeas = (ideas: readonly TripOverviewIdeaInput[]): IdeasSection => ({
	count: ideas.length,
	items: ideas.slice(0, MAX_PREVIEW_IDEAS).map(
		(idea): OverviewIdea => ({
			id: String(idea.id),
			title: idea.name,
			thumbnailUrl: idea.thumbnailUrl ?? null,
		}),
	),
})

const buildAccommodations = (stays: readonly TripOverviewStayInput[]): AccommodationsSection => ({
	count: stays.length,
	// Chronological rows: UTC "YYYY-MM-DD HH:MM:SS" strings sort lexicographically.
	items: [...stays]
		.sort((a, b) => (a.checkInDate ?? '').localeCompare(b.checkInDate ?? ''))
		.slice(0, MAX_PREVIEW_STAYS)
		.map(
			(stay): OverviewStay => ({
				id: String(stay.id),
				name: stay.name,
				city: stay.city ?? null,
				photoUrl: stay.photoUrl ?? null,
				dateRange: stayDateRange(stay.checkInDate, stay.checkOutDate),
			}),
		),
})

const buildFlights = (flights: readonly TripOverviewFlightInput[]): FlightsSection => ({
	count: flights.length,
	firstFlight: flights.length > 0 ? toOverviewFlight(flights[0]) : null,
})

const buildFiles = (files: readonly TripOverviewFileInput[]): FilesSection => ({
	count: files.length,
	items: files.slice(0, MAX_PREVIEW_FILES).map(
		(file): OverviewFile => ({
			id: file.id,
			fileName: file.fileName,
			url: file.url ?? null,
			thumbnailUrl: file.thumbnailUrl ?? null,
			isPdf: isPdfMime(file.mimeType),
		}),
	),
})

/** Expense/pay-up totals + counts, summed in the trip default currency only. */
const buildMoney = (money: TripOverviewMoneyInput | null | undefined): MoneySection => {
	const currencyCode = money?.defaultCurrencyCode || 'usd'
	const expenses = money?.expenses ?? []
	const payUps = money?.payUps ?? []

	return {
		expenseCount: expenses.length,
		hasExpenses: expenses.length > 0,
		currencyCode,
		expenseTotal: formatMoney(sumInCurrency(expenses, currencyCode), currencyCode),
		payUpCount: payUps.length,
		payUpTotal: formatMoney(sumInCurrency(payUps, currencyCode), currencyCode),
	}
}

/** Normalize the trip-home hook responses into the bento's `TripOverviewData`. */
export const buildTripOverviewData = (input: BuildTripOverviewInput): TripOverviewData => {
	const { trip, members, events } = input

	const name = trip?.name ?? ''
	const startDate = trip?.startDate ?? null
	const dayCount = trip?.dayCount ?? 0
	const hasDates = isValidCalendarDate(startDate) && dayCount > 0

	const memberList = members ?? []
	const travelers = memberList.map(toTraveler)
	const eventList = events ?? []
	const previewDays = buildPreviewDays(startDate, dayCount, eventList, input.travelSegments ?? [])

	return {
		hero: {
			name,
			dayCount,
			hasDates,
			dateRange: buildDateRange(startDate, dayCount),
			travelers,
			memberCount: travelers.length,
		},
		itinerary: {
			hasDates,
			dayCount,
			// In-range only, so `eventCount > 0` implies the columns carry a card.
			eventCount: eventList.filter(event => isOnTripDay(event, dayCount)).length,
			previewDays,
		},
		ideas: buildIdeas(input.ideas ?? []),
		accommodations: buildAccommodations(input.accommodations ?? []),
		flights: buildFlights(input.flights ?? []),
		files: buildFiles(input.files ?? []),
		money: buildMoney(input.money),
	}
}
